import {
  Inject,
  LinkAnnotation,
  Magnification,
  Navigation,
  PdfViewerComponent,
  Print,
  TextSearch,
  TextSelection,
  Toolbar,
} from '@syncfusion/ej2-react-pdfviewer'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import styles from './preview.module.scss'

const getPdfViewerResourceUrl = () => {
  const basePath = import.meta.env.BASE_URL || '/'
  const normalizedBase = basePath.endsWith('/') ? basePath : `${basePath}/`

  return new URL('ej2-pdfviewer-lib', `${window.location.origin}${normalizedBase}`).href
}

const isViewerDomReady = (viewer: PdfViewerComponent | null) => {
  if (!viewer?.viewerBase?.viewerContainer || !viewer.element) {
    return false
  }

  return viewer.element.clientWidth > 0 && viewer.element.clientHeight > 0
}

type Props = {
  pdfUrl: string
  documentId: number
  scrollMode?: 'self' | 'parent'
}

export const SyncfusionPdfViewer = ({ pdfUrl, documentId }: Props) => {
  const viewerId = useId().replace(/:/g, '')
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<PdfViewerComponent>(null)
  const loadedPdfUrlRef = useRef<string | null>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const resourceUrl = useMemo(() => getPdfViewerResourceUrl(), [])
  const interactionMode = useMemo(
    () => (navigator.maxTouchPoints > 0 ? 'Pan' : 'TextSelection'),
    [],
  )

  const hasContainerSize = containerSize.width > 0 && containerSize.height > 0

  const tryLoadDocument = useCallback(() => {
    const viewer = viewerRef.current

    if (!pdfUrl || !viewer || loadedPdfUrlRef.current === pdfUrl) {
      return isViewerDomReady(viewer)
    }

    if (!isViewerDomReady(viewer)) {
      return false
    }

    loadedPdfUrlRef.current = pdfUrl
    viewer.load(pdfUrl, null)
    return true
  }, [pdfUrl])

  useEffect(() => {
    loadedPdfUrlRef.current = null
  }, [documentId, pdfUrl])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const updateContainerSize = () => {
      const { clientWidth, clientHeight } = container
      if (clientWidth > 0 && clientHeight > 0) {
        setContainerSize((current) => {
          if (current.width === clientWidth && current.height === clientHeight) {
            return current
          }

          return { width: clientWidth, height: clientHeight }
        })
      }
    }

    updateContainerSize()

    const resizeObserver = new ResizeObserver(updateContainerSize)
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!pdfUrl || !hasContainerSize) {
      return
    }

    let cancelled = false
    let frameId = 0
    let attempts = 0

    const attemptLoad = () => {
      if (cancelled) {
        return
      }

      if (tryLoadDocument()) {
        return
      }

      attempts += 1
      if (attempts < 300) {
        frameId = requestAnimationFrame(attemptLoad)
      }
    }

    frameId = requestAnimationFrame(attemptLoad)

    return () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    }
  }, [hasContainerSize, pdfUrl, documentId, tryLoadDocument])

  return (
    <div ref={containerRef} className={styles.syncfusionViewer}>
      {hasContainerSize && (
        <PdfViewerComponent
          key={documentId}
          ref={viewerRef}
          id={`labs-pdf-viewer-${viewerId}`}
          resourceUrl={resourceUrl}
          height={containerSize.height}
          width={containerSize.width}
          enableToolbar
          enableThumbnail={false}
          enableBookmark={false}
          interactionMode={interactionMode}
          zoomMode='FitToPage'
          toolbarSettings={{
            toolbarItems: ['PageNavigationTool', 'MagnificationTool', 'PanTool'],
          }}>
          <Inject
            services={[
              Toolbar,
              Magnification,
              Navigation,
              LinkAnnotation,
              Print,
              TextSelection,
              TextSearch,
            ]}
          />
        </PdfViewerComponent>
      )}
    </div>
  )
}
